import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ChevronRight, Building2, Target, Users, Warehouse, CheckCircle2, Package, Award, Phone, Mail, TrendingUp, ShieldCheck, Clock, Heart, Briefcase, Eye } from 'lucide-react';
import { useSeo } from '../hooks/useSeo';
import {
  careersService, JobPositionCard, CONTRACT_LABELS, DEPARTMENT_LABELS, REMOTE_LABELS,
} from '../services/careersService';

const PAGE_ICONS = {
  'chi-siamo': Building2,
  'mission': Target,
  'disponibilita-logistica': Warehouse,
  'area-commerciale': Briefcase,
  'lavora-con-noi': Users,
};

const NAV_ITEMS = [
  { id: 'chi-siamo', title: 'Chi Siamo' },
  { id: 'mission', title: 'La Nostra Mission' },
  { id: 'disponibilita-logistica', title: 'Logistica DTK' },
  { id: 'area-commerciale', title: 'Area Commerciale' },
  { id: 'lavora-con-noi', title: 'Lavora con Noi' },
];

const IMG_TEAM = 'https://customer-assets-39nsmqrw.emergentagent.net/job_e1a8c620-0cbd-4e02-a8b8-f4a98268bc01/artifacts/ppwgvxj6_Chi%20Siamo.png';
const IMG_MISSION = 'https://customer-assets-39nsmqrw.emergentagent.net/job_e1a8c620-0cbd-4e02-a8b8-f4a98268bc01/artifacts/3i5ohe6p_Mission_%20i%20dettagli%20contano%20sempre.jpg';
const IMG_LOGISTICA = 'https://customer-assets-39nsmqrw.emergentagent.net/job_e1a8c620-0cbd-4e02-a8b8-f4a98268bc01/artifacts/g0unrede_Logistica%20Interna.png';
const IMG_LOGISTICA_NUMERI = 'https://customer-assets-39nsmqrw.emergentagent.net/job_e1a8c620-0cbd-4e02-a8b8-f4a98268bc01/artifacts/9o1q1zho_Logistica%20Interna_%20numeri.png';
const IMG_SALES = 'https://customer-assets-39nsmqrw.emergentagent.net/job_e1a8c620-0cbd-4e02-a8b8-f4a98268bc01/artifacts/8ufc8pmr_Area%20Commerciale.png';

function ChiSiamo() {
  useSeo(
    'DTK | Toner e ricambi per stampanti e fotocopiatrici',
    'Da oltre 25 anni DTK distribuisce toner, consumabili, gruppi completi e ricambi per stampanti, fotocopiatrici e multifunzione.'
  );

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight mb-4">Specialisti nella distribuzione per il printing professionale</h1>
        <p className="text-base leading-relaxed text-muted-foreground">
          DTK è un&apos;azienda specializzata nella distribuzione di consumabili, gruppi completi, componenti e ricambi per stampanti, fotocopiatrici e sistemi multifunzione. Da oltre 25 anni affianchiamo rivenditori, centri di assistenza tecnica, aziende di noleggio, operatori del printing e professionisti della rigenerazione, offrendo un assortimento completo di prodotti originali e compatibili per i principali marchi presenti sul mercato.
        </p>
        <p className="text-base leading-relaxed text-muted-foreground mt-3">
          La profonda conoscenza del settore ci permette di selezionare soluzioni affidabili, individuare rapidamente il prodotto corretto e supportare i nostri clienti anche nelle richieste più tecniche o meno comuni. In DTK, disponibilità dei prodotti, competenza e rapidità non sono semplici servizi accessori: sono parte integrante del nostro modo di lavorare.
        </p>
      </div>

      <div className="rounded-xl overflow-hidden aspect-[16/9]">
        <img src={IMG_TEAM} alt="Team DTK specializzato nella distribuzione di toner e ricambi per stampanti" className="w-full h-full object-cover" />
      </div>

      {/* Stats */}
      <div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { num: '25+', label: 'Anni di esperienza', icon: Clock },
            { num: '1000+', label: 'Clienti ogni anno', icon: Users },
            { num: '10.000+', label: 'Ordini gestiti all\u2019anno', icon: Package },
            { num: '92%', label: 'Clienti ricorrenti', icon: TrendingUp },
          ].map(s => (
            <div key={s.label} className="rounded-xl border bg-[hsl(var(--dtk-canvas))] p-4 text-center">
              <s.icon className="w-5 h-5 text-[hsl(var(--dtk-focus))] mx-auto mb-2" />
              <div className="text-2xl font-bold text-[hsl(214,58%,41%)]">{s.num}</div>
              <div className="text-xs text-muted-foreground mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h2 className="text-lg font-semibold mb-3">Un unico partner per il mondo della stampa</h2>
        <p className="text-base leading-relaxed text-muted-foreground mb-3">
          Il nostro catalogo comprende:
        </p>
        <ul className="space-y-2 mb-3">
          {[
            'toner e cartucce laser;',
            'gruppi fusore, gruppi tamburo, gruppi sviluppo e gruppi di trasferimento;',
            'ricambi per stampanti e fotocopiatrici, tra cui tamburi, developer, rulli, lame, ingranaggi e componenti meccanici;',
            'attrezzature e componenti per la rigenerazione e la manutenzione.',
          ].map(item => (
            <li key={item} className="flex items-start gap-2 text-base leading-relaxed text-muted-foreground">
              <span className="w-1.5 h-1.5 rounded-full bg-[hsl(214,58%,41%)] mt-2.5 flex-shrink-0" />
              {item}
            </li>
          ))}
        </ul>
        <p className="text-base leading-relaxed text-muted-foreground">
          Un&apos;offerta ampia e specializzata, pensata per permettere ai clienti professionali di concentrare gli acquisti presso un unico interlocutore, ridurre i tempi di ricerca prodotti e gestire con maggiore semplicità ed economicità il parco macchine.
        </p>
      </div>

      <div>
        <h2 className="text-lg font-semibold mb-3">Competenza che diventa servizio</h2>
        <p className="text-base leading-relaxed text-muted-foreground">
          Conoscere un prodotto significa comprenderne l&apos;applicazione, le compatibilità e il ruolo all&apos;interno dell&apos;apparecchiatura.
        </p>
        <p className="text-base leading-relaxed text-muted-foreground mt-3">
          Per questo il valore di DTK non si limita alla semplice fornitura di una referenza. Partiamo dal codice, dal modello della macchina o dall&apos;esigenza tecnica per aiutare il cliente a identificare la soluzione più adatta, valutando anche eventuali prodotti alternativi in base alle necessità tecniche ed economiche.
        </p>
      </div>

      {/* Punti di Forza */}
      <div>
        <h2 className="text-lg font-semibold mb-4">I nostri punti di forza</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[
            'Esperienza pluriennale nel printing professionale',
            'Ampio assortimento di prodotti originali e compatibili',
            'Supporto di primo livello nella ricerca di codici e compatibilità',
            'Selezione continua dei prodotti per qualità',
            'Prezzi competitivi per il rivenditore',
            'E-commerce B2B semplice, rapido ed intuitivo',
            'Aggiornamento continuo del catalogo',
          ].map(p => (
            <div key={p} className="flex items-start gap-3 p-3 rounded-lg border bg-white">
              <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
              <span className="text-sm">{p}</span>
            </div>
          ))}
        </div>
        <p className="text-base text-muted-foreground mt-4">
          Cerchi un prodotto specifico? <Link to="/contatti" className="text-[hsl(214,58%,41%)] font-medium hover:underline">Contattaci</Link>: il nostro team ti aiuterà a individuare la referenza corretta.
        </p>
      </div>

      {/* Collegamenti interni */}
      <div>
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">Scopri il catalogo</h2>
        <div className="flex flex-wrap gap-2">
          {[
            { label: 'Toner e cartucce laser', href: '/i-nostri-prodotti/toner-cartucce-laser' },
            { label: 'Gruppi completi', href: '/i-nostri-prodotti/gruppi-completi' },
            { label: 'Ricambi per stampanti e fotocopiatrici', href: '/i-nostri-prodotti/ricambi-stampanti-fotocopiatrici' },
            { label: 'Attrezzature tecniche', href: '/i-nostri-prodotti/attrezzature-tecniche' },
            { label: 'Logistica DTK', href: '/azienda/disponibilita-logistica' },
            { label: 'Il marchio Ktek', href: '/ktek' },
          ].map(l => (
            <Link key={l.href} to={l.href} className="text-sm px-3 py-1.5 rounded-md border bg-white hover:border-[hsl(var(--dtk-focus))] hover:text-[hsl(214,58%,41%)] transition-colors">
              {l.label}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

function Mission() {
  useSeo(
    'La nostra mission | DTK Printing Professionale',
    'Competenza, affidabilità e convenienza al servizio di rivenditori, centri assistenza, noleggiatori e professionisti della stampa.'
  );

  const principi = [
    { icon: ShieldCheck, title: 'Affidabilità', bold: 'Ogni prodotto che consegniamo determina il lavoro di chi ci sceglie.', rest: 'Per questo selezioniamo solo ciò che garantisce continuità e risultati certi.' },
    { icon: Eye, title: 'Trasparenza', bold: 'Crediamo nelle relazioni costruite nel tempo.', rest: 'Comunicazione chiara, disponibilità reale e promesse mantenute sono per noi il fondamento di ogni relazione commerciale.' },
    { icon: Award, title: 'Competenza', bold: 'Da oltre 25 anni viviamo il mondo del printing.', rest: 'Conosciamo le criticità, i ritmi, le esigenze quotidiane dei nostri clienti e li supportiamo con soluzioni su misura.' },
    { icon: Heart, title: 'Cura per il cliente', bold: 'Non siamo semplici fornitori: siamo partner.', rest: 'Ascoltiamo, analizziamo, proponiamo. Diamo il massimo per supportare il tuo business. Sempre.' },
  ];

  return (
    <div className="space-y-8">
      <div className="rounded-xl overflow-hidden aspect-[16/9]">
        <img src={IMG_MISSION} alt="I dettagli contano sempre: la mission DTK per il printing professionale" className="w-full h-full object-cover" />
      </div>

      <div>
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight mb-4">I dettagli contano. Sempre.</h1>
        <p className="text-base leading-relaxed text-muted-foreground">
          La nostra missione aziendale è rendere più semplice, affidabile e conveniente il lavoro dei professionisti della stampa.
        </p>
        <p className="text-base leading-relaxed text-muted-foreground mt-3">
          Nel settore dell&apos;office automation, ogni dettaglio può incidere sulla continuità del servizio e la soddisfazione del cliente: la qualità di un componente, la disponibilità di una referenza, la rapidità di una risposta o la puntualità di una consegna. Per questo selezioniamo prodotti affidabili, costruiamo un assortimento specializzato e affianchiamo i nostri clienti con un supporto rapido e competente.
        </p>
        <p className="text-base leading-relaxed text-muted-foreground mt-3">
          Ogni ordine rappresenta molto più di una transazione commerciale: è un segno di fiducia ed una responsabilità concreta. Sappiamo che, scegliendo DTK, i nostri clienti ci affidano una parte del proprio lavoro quotidiano e, indirettamente, la soddisfazione dei loro clienti.
        </p>
        <p className="text-base leading-relaxed text-muted-foreground mt-3">
          Affrontiamo quindi ogni richiesta con attenzione, responsabilità e trasparenza, curando ogni fase della fornitura per rendere ogni passaggio più semplice, efficiente e sicuro. Perché, nel nostro lavoro, <strong className="font-semibold text-foreground">i dettagli contano sempre</strong>.
        </p>
      </div>

      <div>
        <h2 className="text-lg font-semibold mb-4">Il nostro modo di creare valore</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {principi.map(v => (
            <div key={v.title} className="rounded-xl border bg-white p-5">
              <div className="w-10 h-10 rounded-lg bg-[hsl(210,40%,93%)] flex items-center justify-center mb-3">
                <v.icon className="w-5 h-5 text-[hsl(214,58%,41%)]" />
              </div>
              <h3 className="text-lg font-semibold mb-1.5">{v.title}</h3>
              <p className="text-sm text-muted-foreground"><strong className="font-semibold text-foreground">{v.bold}</strong> {v.rest}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function LavoraConNoi() {
  const [posizioni, setPosizioni] = React.useState<JobPositionCard[]>([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let alive = true;
    careersService.listPublic()
      .then(d => { if (alive) setPosizioni(d.items); })
      .catch(() => { if (alive) setPosizioni([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight mb-4">Lavora con Noi</h1>
        <p className="text-base leading-relaxed text-muted-foreground">
          DTK è un&apos;azienda dinamica ed in forte crescita. Siamo sempre alla ricerca di persone propositive, orientate al risultato ed appassionate di soluzioni per la stampa e l&apos;office automation.
        </p>
      </div>

      <div>
        <h2 className="text-lg font-semibold mb-4">Posizioni Aperte</h2>
        {loading ? (
          <div className="space-y-3" data-testid="careers-public-loading">
            {[0, 1, 2].map(i => <div key={i} className="rounded-xl border bg-white p-5 h-[74px] animate-pulse" />)}
          </div>
        ) : posizioni.length === 0 ? (
          <div className="rounded-xl border border-dashed bg-[hsl(var(--dtk-canvas))] p-6 text-center" data-testid="careers-public-empty">
            <p className="text-sm text-muted-foreground">
              Al momento non ci sono posizioni aperte. Puoi comunque inviarci una candidatura spontanea: valutiamo tutti i profili.
            </p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="careers-public-list">
            {posizioni.map(p => (
              <Link
                key={p.id}
                to={`/azienda/lavora-con-noi/${p.slug}`}
                className="rounded-xl border bg-white p-5 flex items-center justify-between hover:border-[hsl(var(--dtk-focus))] transition-colors group"
                data-testid={`careers-public-card-${p.slug}`}
              >
                <div>
                  <h3 className="text-sm font-semibold group-hover:text-[hsl(214,58%,41%)]">{p.titolo}</h3>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <span className="text-xs px-2 py-0.5 rounded-md border bg-secondary/60">{CONTRACT_LABELS[p.tipoContratto]}</span>
                    <span className="text-xs text-muted-foreground">{DEPARTMENT_LABELS[p.area]}</span>
                    <span className="text-xs text-muted-foreground">· {p.sede}</span>
                    {p.sedeRemoto !== 'in_sede' && <span className="text-xs text-muted-foreground">· {REMOTE_LABELS[p.sedeRemoto]}</span>}
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-[hsl(var(--dtk-focus))]" />
              </Link>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-xl border bg-[hsl(var(--dtk-canvas))] p-6">
        <h2 className="text-sm font-semibold mb-2">Candidatura Spontanea</h2>
        <p className="text-sm text-muted-foreground mb-4">
          Non trovi la posizione giusta? Invia comunque il tuo CV. Valutiamo tutte le candidature e le archiviamo per posizioni future.
        </p>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 text-sm">
            <Mail className="w-4 h-4 text-[hsl(var(--dtk-focus))]" />
            <a href="mailto:info@dtksrl.eu" className="text-[hsl(var(--dtk-focus))] hover:underline font-mono text-xs">info@dtksrl.eu</a>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <Phone className="w-4 h-4 text-muted-foreground" />
            <span className="text-xs">06/5135753</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function DisponibilitaLogistica() {
  useSeo(
    'Logistica DTK | 11.000 referenze e consegne 24/48h',
    'Magazzino con oltre 11.000 referenze, disponibilità superiore al 95% e consegne rapide in tutta Italia entro 24/48 ore.'
  );

  return (
    <div className="space-y-8">
      <div className="rounded-xl overflow-hidden aspect-[16/9]">
        <img src={IMG_LOGISTICA} alt="Magazzino e logistica interna DTK: preparazione e spedizione ordini" className="w-full h-full object-cover" />
      </div>

      <div>
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight mb-4">Consegne veloci. <span className="text-[hsl(214,58%,41%)]">Sempre.</span></h1>
        <p className="text-base leading-relaxed text-muted-foreground">
          Con oltre 11.000 referenze e una disponibilità della merce costantemente superiore al 95%, il magazzino DTK è riconosciuto tra i più forniti in Italia nel settore dei toner e ricambi per stampanti, fotocopiatrici e multifunzione.
        </p>
        <p className="text-base leading-relaxed text-muted-foreground mt-3">
          Monitoriamo costantemente l&apos;andamento della domanda e la rotazione degli articoli per mantenere livelli di stock coerenti con le reali esigenze del mercato e garantire quindi una disponibilità immediata.
        </p>
        <p className="text-base leading-relaxed text-muted-foreground mt-3">
          I nostri clienti ricevono gli ordini in tutta Italia generalmente entro 24/48 ore, grazie all&apos;ampiezza delle scorte e a un&apos;organizzazione logistica orientata alle esigenze del cliente.
        </p>
      </div>

      <div>
        <h2 className="text-lg font-semibold mb-3">Una logistica gestita direttamente da noi</h2>
        <p className="text-base leading-relaxed text-muted-foreground">
          La gestione interna del magazzino e della logistica ci permette di operare direttamente in ogni fase dell&apos;ordine: dal prelievo del materiale richiesto fino alla preparazione della spedizione.
        </p>
        <p className="text-base leading-relaxed text-muted-foreground mt-3 mb-3">
          Questo ci consente di garantire elevati livelli di flessibilità e performance di servizio. Manteniamo infatti gli stessi tempi di evasione garantendo allo stesso tempo una gestione efficiente di:
        </p>
        <ul className="space-y-2">
          {[
            'ordini urgenti;',
            'integrazioni o modifiche;',
            'spedizioni in dropshipping;',
            'richieste personalizzate.',
          ].map(item => (
            <li key={item} className="flex items-start gap-2 text-base leading-relaxed text-muted-foreground">
              <span className="w-1.5 h-1.5 rounded-full bg-[hsl(214,58%,41%)] mt-2.5 flex-shrink-0" />
              {item}
            </li>
          ))}
        </ul>
      </div>

      <div className="flex flex-col sm:flex-row items-center gap-8">
        <div className="w-full sm:w-96 flex-shrink-0">
          <img src={IMG_LOGISTICA_NUMERI} alt="Consegne DTK in tutta Italia" className="w-full h-auto object-contain" />
        </div>
        <div className="flex-1">
          <h2 className="text-2xl font-bold mb-5">I numeri della logistica DTK</h2>
          <div className="space-y-5">
            {[
              { num: '11.000+', label: 'Referenze disponibili' },
              { num: '95%+', label: 'Disponibilità media della merce' },
              { num: '24/48H', label: 'Consegna in tutta Italia' },
            ].map(s => (
              <div key={s.label}>
                <div className="text-2xl font-bold">{s.num}</div>
                <div className="text-base text-muted-foreground">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <p className="text-base text-muted-foreground">
        Hai una richiesta particolare relativa alle spedizioni? <Link to="/contatti" className="text-[hsl(214,58%,41%)] font-medium hover:underline">Contattaci</Link> per individuare insieme la soluzione logistica più adatta.
      </p>
    </div>
  );
}

function AreaCommerciale() {
  useSeo(
    'Area Commerciale DTK | Consulenza e servizio su misura',
    'Il team commerciale DTK ti affianca con attenzione e trasparenza per ottimizzare fornitura, ordini e condizioni commerciali su misura per il tuo business.'
  );

  return (
    <div className="space-y-8">
      <div className="rounded-xl overflow-hidden aspect-[16/9]">
        <img src={IMG_SALES} alt="Il team commerciale DTK a disposizione dei clienti" className="w-full h-full object-cover" />
      </div>

      <div>
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight mb-4">Area Commerciale</h1>
        <p className="text-base leading-relaxed text-muted-foreground">
          Di primaria importanza è per noi, instaurare con il cliente un rapporto di fiducia basato sulla trasparenza reciproca, <strong className="font-semibold text-foreground">destinato a crescere e consolidarsi nel tempo</strong>.
        </p>
        <p className="text-base leading-relaxed text-muted-foreground mt-3">
          Siamo pronti ad accompagnarti nel rendere più <strong className="font-semibold text-foreground">comoda</strong> la gestione delle tue forniture, per una gestione sana e semplice del tuo parco macchine.
        </p>
        <p className="text-base leading-relaxed text-muted-foreground mt-3">
          Ascoltiamo con attenzione le tue esigenze per <strong className="font-semibold text-foreground">ottimizzare ogni aspetto della collaborazione commerciale</strong>: dalla selezione dei prodotti alle condizioni di fornitura, fino alla gestione degli ordini, delle disponibilità e delle consegne. L&apos;obiettivo è costruire un servizio realmente su misura, capace di supportare concretamente il tuo business.
        </p>
        <p className="text-base leading-relaxed text-muted-foreground mt-3">
          Desideri essere contattato da un nostro referente commerciale? <Link to="/contatti" className="font-semibold text-[hsl(214,58%,41%)] hover:underline">Lascia un messaggio</Link>
        </p>
      </div>
    </div>
  );
}

const PAGE_COMPONENTS = {
  'chi-siamo': ChiSiamo,
  'mission': Mission,
  'disponibilita-logistica': DisponibilitaLogistica,
  'area-commerciale': AreaCommerciale,
  'lavora-con-noi': LavoraConNoi,
};

export default function CompanyPage() {
  const { pageId } = useParams();
  if (!pageId) return <Navigate to="/azienda/chi-siamo" replace />;

  const PageComponent = PAGE_COMPONENTS[pageId];
  const Icon = PAGE_ICONS[pageId] || Building2;

  if (!PageComponent) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-20 text-center">
        <h1 className="text-2xl font-semibold">Pagina non trovata</h1>
        <Link to="/" className="text-sm text-[hsl(var(--dtk-focus))] mt-4 inline-block hover:underline">Torna alla home</Link>
      </div>
    );
  }

  return (
    <div data-testid="company-page">
      <div className="border-b bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-2 text-sm text-muted-foreground">
          <Link to="/" className="hover:text-foreground transition-colors">Home</Link>
          <ChevronRight className="w-3 h-3" />
          <span>Azienda</span>
          <ChevronRight className="w-3 h-3" />
          <span className="text-foreground font-medium">{NAV_ITEMS.find(n => n.id === pageId)?.title}</span>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* order: su mobile la navigazione di sezione deve seguire il
              contenuto, altrimenti il titolo della pagina finisce dopo
              ~430px di menu. Da lg in su l'ordine torna quello del DOM. */}
          <aside className="order-2 lg:order-none lg:w-56 flex-shrink-0">
            <div className="rounded-xl border bg-white p-4 sticky top-20">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Profilo Azienda</h3>
              <div className="space-y-1">
                {NAV_ITEMS.map(p => {
                  const PIcon = PAGE_ICONS[p.id] || Building2;
                  return (
                    <Link key={p.id} to={`/azienda/${p.id}`}
                      className={`tap flex items-center gap-2 px-3 py-2.5 lg:py-2 rounded-lg text-sm transition-colors duration-150 ${
                        p.id === pageId ? 'bg-[hsl(210,40%,93%)] text-[hsl(214,58%,41%)] font-medium' : 'text-muted-foreground hover:text-foreground hover:bg-secondary/60'
                      }`}
                    >
                      <PIcon className="w-4 h-4 flex-shrink-0" />
                      {p.title}
                    </Link>
                  );
                })}
              </div>
            </div>
          </aside>

          <div className="order-1 lg:order-none flex-1 min-w-0">
            <div className="rounded-xl border bg-white p-6 sm:p-8">
              <PageComponent />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
